/**
 * Sentry Error Monitoring Configuration
 *
 * Initializes Sentry for error tracking in the React dashboard.
 * Should be called BEFORE React renders in main.tsx.
 *
 * @module lib/sentry
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking.
 * Only initializes in staging/production environments when DSN is provided.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;

  // Skip initialization if no DSN (local development)
  if (!dsn) {
    console.log('[Sentry] Skipping initialization - no DSN configured');
    return;
  }

  Sentry.init({
    dsn,
    environment,

    // Performance monitoring sample rate (10% of transactions in production)
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Session replay for debugging (only in staging/production)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Only send errors in staging/production
    enabled: environment !== 'development',

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Filter out known non-actionable errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Filter out network errors that are expected
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        return null;
      }

      // Filter out Firebase auth errors that users can resolve
      if (
        error instanceof Error &&
        (error.message.includes('auth/popup-closed-by-user') ||
          error.message.includes('auth/cancelled-popup-request'))
      ) {
        return null;
      }

      return event;
    },

    // Ignore specific URLs (e.g., browser extensions)
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });

  console.log(`[Sentry] Initialized for ${environment}`);
}

/**
 * Set user context for error tracking.
 * Call this after user authentication.
 */
export function setSentryUser(user: { id: string; email?: string | null }): void {
  Sentry.setUser({
    id: user.id,
    email: user.email || undefined,
  });
}

/**
 * Set organization context for error tracking.
 * Call this after organization is loaded.
 */
export function setSentryOrganization(org: { id: string; name: string; planId: string }): void {
  Sentry.setTag('organization_id', org.id);
  Sentry.setTag('organization_name', org.name);
  Sentry.setTag('plan_id', org.planId);
}

/**
 * Clear user context (on logout).
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/**
 * Capture an exception manually.
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>
): void {
  Sentry.captureException(error, {
    extra: context,
  });
}

/**
 * Capture a message for logging purposes.
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  Sentry.captureMessage(message, level);
}

// Re-export Sentry's ErrorBoundary for convenience
export { ErrorBoundary } from '@sentry/react';
